import { useState, useRef, useEffect } from 'react';
import { Filter } from 'lucide-react';
import { InstanceEventType, type InstanceEvent } from '../types/instance';

interface TeamActivityEvent extends InstanceEvent {
  agentName: string;
}

const EVENT_TYPE_STYLES: Record<string, { dot: string; label: string }> = {
  [InstanceEventType.MESSAGE]: { dot: 'bg-blue-500', label: 'Message' },
  [InstanceEventType.TOOL_USE]: { dot: 'bg-purple-500', label: 'Tool Use' },
  [InstanceEventType.TOOL_RESULT]: { dot: 'bg-indigo-400', label: 'Tool Result' },
  [InstanceEventType.ERROR]: { dot: 'bg-red-500', label: 'Error' },
  [InstanceEventType.STATE_CHANGE]: { dot: 'bg-yellow-500', label: 'State Change' },
  [InstanceEventType.PROGRESS]: { dot: 'bg-green-500', label: 'Progress' },
};

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function truncateContent(content: string | null, maxLen = 200): string {
  if (!content) return '';
  return content.length > maxLen ? content.slice(0, maxLen) + '...' : content;
}

interface TeamActivityProps {
  events: TeamActivityEvent[];
}

export type { TeamActivityEvent };

export default function TeamActivity({ events }: TeamActivityProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const [typeFilter, setTypeFilter] = useState<string>('');

  useEffect(() => {
    if (autoScroll && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [events, autoScroll]);

  const filtered = typeFilter
    ? events.filter(e => e.eventType === typeFilter)
    : events;

  const eventTypes = Object.entries(EVENT_TYPE_STYLES);

  return (
    <div className="flex flex-col rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-slate-700">Activity</span>
          <div className="flex items-center gap-1">
            <Filter className="h-3 w-3 text-slate-400" />
            <select
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
              className="rounded border-0 bg-transparent py-0 pl-1 pr-6 text-xs text-slate-500 focus:outline-none focus:ring-0"
            >
              <option value="">All events</option>
              {eventTypes.map(([key, { label }]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
        </div>
        <label className="flex items-center gap-1.5 text-xs text-slate-400">
          <input
            type="checkbox"
            checked={autoScroll}
            onChange={e => setAutoScroll(e.target.checked)}
            className="rounded border-slate-300"
          />
          Auto-scroll
        </label>
      </div>

      <div ref={containerRef} className="flex-1 overflow-y-auto px-4 py-3" style={{ maxHeight: '400px', minHeight: '200px' }}>
        {filtered.length === 0 ? (
          <div className="py-8 text-center text-sm text-slate-400">
            {typeFilter ? 'No events match this filter' : 'No activity yet'}
          </div>
        ) : (
          <div className="relative ml-3 border-l border-slate-200 pl-6">
            {filtered.map(event => {
              const style = EVENT_TYPE_STYLES[event.eventType] ?? { dot: 'bg-slate-400', label: event.eventType };
              const content = event.toolName
                ? `${event.toolName}${event.content ? ': ' + truncateContent(event.content) : ''}`
                : truncateContent(event.content);

              return (
                <div key={event.id} className="relative pb-4 last:pb-0">
                  {/* Timeline dot */}
                  <div className={`absolute -left-[31px] top-1 h-2.5 w-2.5 rounded-full ${style.dot} ring-2 ring-white`} />

                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-xs">
                        <span className="font-medium text-slate-700">{event.agentName}</span>
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">
                          {style.label}
                        </span>
                      </div>
                      {content && (
                        <p className="mt-1 break-words text-sm text-slate-600">{content}</p>
                      )}
                    </div>
                    <span className="shrink-0 text-xs text-slate-400">{formatTime(event.createdAt)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
